import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";

type PeriodKey = "today" | "7d" | "30d" | "month" | "custom";

interface DateRange {
  start: string;
  end: string;
}

interface Order {
  id: string;
  date: string; // ISO
  orderNumber: string;
  product: string;
  variation: string;
  value: number;
  shopeeFees: number;
  releasedValue: number;
  cost: number;
}

interface MonthlyProfitPoint {
  monthLabel: string;
  gross: number;
  net: number;
}

interface WeeklyProfitPoint {
  weekLabel: string;
  gross: number;
  net: number;
}

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function calcMargin(net: number, gross: number): number {
  if (gross === 0) return 0;
  return (net / gross) * 100;
}

// ----- Mock de dados (até integrar com API Shopee) -----

const mockOrders: Order[] = [
  {
    id: "1",
    date: "2025-11-28",
    orderNumber: "SP-102938",
    product: "Kit Luar 24 unidades",
    variation: "24 unid",
    value: 259.9,
    shopeeFees: 42.5,
    releasedValue: 217.4,
    cost: 120,
  },
  {
    id: "2",
    date: "2025-11-27",
    orderNumber: "SP-102937",
    product: "Kit Luar 30 unidades",
    variation: "30 unid",
    value: 299.9,
    shopeeFees: 48.9,
    releasedValue: 251,
    cost: 145,
  },
  {
    id: "3",
    date: "2025-11-26",
    orderNumber: "SP-102936",
    product: "Kit Luar 24 unidades",
    variation: "24 unid",
    value: 259.9,
    shopeeFees: 42.5,
    releasedValue: 217.4,
    cost: 120,
  },
  {
    id: "4",
    date: "2025-11-25",
    orderNumber: "SP-102935",
    product: "Kit Luar 30 unidades",
    variation: "30 unid",
    value: 299.9,
    shopeeFees: 48.9,
    releasedValue: 251,
    cost: 145,
  },
  {
    id: "5",
    date: "2025-11-24",
    orderNumber: "SP-102934",
    product: "Kit Luar 24 unidades",
    variation: "24 unid",
    value: 259.9,
    shopeeFees: 42.5,
    releasedValue: 217.4,
    cost: 120,
  },
  // pode adicionar mais se quiser
];

const mockSaldoDisponivel = 4532.9;
const mockSaldoALiberar = 1789.4;
const mockPrevisaoProximoSaque = 6231.5;

const mockSaques = [
  { date: "22/11/2025", value: 3500 },
  { date: "15/11/2025", value: 2980 },
  { date: "08/11/2025", value: 4120 },
];

const mockMonthlyProfit: MonthlyProfitPoint[] = [
  { monthLabel: "Dez/24", gross: 5200, net: 3100 },
  { monthLabel: "Jan/25", gross: 6100, net: 3550 },
  { monthLabel: "Fev/25", gross: 5800, net: 3390 },
  { monthLabel: "Mar/25", gross: 6400, net: 3720 },
  { monthLabel: "Abr/25", gross: 6900, net: 4010 },
  { monthLabel: "Mai/25", gross: 7200, net: 4230 },
  { monthLabel: "Jun/25", gross: 7600, net: 4460 },
  { monthLabel: "Jul/25", gross: 7900, net: 4620 },
  { monthLabel: "Ago/25", gross: 8200, net: 4810 },
  { monthLabel: "Set/25", gross: 8400, net: 4960 },
  { monthLabel: "Out/25", gross: 8800, net: 5150 },
  { monthLabel: "Nov/25", gross: 9100, net: 5320 },
];

const mockWeeklyProfit: WeeklyProfitPoint[] = [
  { weekLabel: "Sem atual", gross: 2300, net: 1280 },
  { weekLabel: "Sem 2", gross: 2100, net: 1180 },
  { weekLabel: "Sem 3", gross: 2500, net: 1370 },
  { weekLabel: "Sem 4", gross: 2700, net: 1490 },
];

// ----------------- Componentes de UI básicos -----------------

interface PeriodSelectorProps {
  period: PeriodKey;
  onChange: (period: PeriodKey) => void;
  customRange: DateRange | null;
  onCustomRangeChange: (range: DateRange) => void;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  period,
  onChange,
  customRange,
  onCustomRangeChange,
}) => {
  const baseBtn =
    "px-3 py-1.5 rounded-lg text-sm transition-colors border border-slate-700";
  const active =
    "bg-emerald-500/10 text-emerald-400 border-emerald-500/60 hover:bg-emerald-500/20";
  const inactive =
    "bg-slate-900/40 text-slate-300 hover:bg-slate-800/70 hover:text-white";

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <button
        className={`${baseBtn} ${period === "today" ? active : inactive}`}
        onClick={() => onChange("today")}
      >
        Hoje
      </button>
      <button
        className={`${baseBtn} ${period === "7d" ? active : inactive}`}
        onClick={() => onChange("7d")}
      >
        7 dias
      </button>
      <button
        className={`${baseBtn} ${period === "30d" ? active : inactive}`}
        onClick={() => onChange("30d")}
      >
        30 dias
      </button>
      <button
        className={`${baseBtn} ${period === "month" ? active : inactive}`}
        onClick={() => onChange("month")}
      >
        Mês
      </button>

      <button
        className={`${baseBtn} ${period === "custom" ? active : inactive}`}
        onClick={() => onChange("custom")}
      >
        Personalizado
      </button>

      {period === "custom" && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300 ml-1">
          <div className="flex items-center gap-1">
            <span>De</span>
            <input
              type="date"
              className="bg-slate-900/60 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-100"
              value={customRange?.start || ""}
              onChange={(e) =>
                onCustomRangeChange({
                  start: e.target.value,
                  end: customRange?.end || e.target.value,
                })
              }
            />
          </div>
          <div className="flex items-center gap-1">
            <span>até</span>
            <input
              type="date"
              className="bg-slate-900/60 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-100"
              value={customRange?.end || ""}
              onChange={(e) =>
                onCustomRangeChange({
                  start: customRange?.start || e.target.value,
                  end: e.target.value,
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  title: string;
  main: string;
  helper?: string;
  helperColor?: "green" | "red" | "muted";
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  main,
  helper,
  helperColor = "muted",
}) => {
  const colorMap: Record<typeof helperColor, string> = {
    green: "text-emerald-400",
    red: "text-rose-400",
    muted: "text-slate-400",
  };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl px-5 py-4 flex flex-col justify-between shadow-lg shadow-black/30">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        {title}
      </p>
      <p className="mt-3 text-2xl font-semibold text-slate-50">{main}</p>
      {helper && (
        <p className={`mt-1 text-[11px] ${colorMap[helperColor]}`}>{helper}</p>
      )}
    </div>
  );
};

// ----------------- Componentes da Dashboard -----------------

const MonthlyProfitChart: React.FC = () => {
  const maxValue = useMemo(
    () =>
      mockMonthlyProfit.reduce(
        (max, item) => Math.max(max, item.gross, item.net),
        0
      ),
    []
  );

  const step = Math.ceil(maxValue / 6 / 500) * 500 || 1000;
  const ticks = Array.from({ length: 7 }, (_, i) => i * step);

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl px-5 py-4 flex flex-col gap-2 shadow-lg shadow-black/30">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-100">Lucro mensal</p>
        <p className="text-[11px] text-slate-400">
          Últimos 12 meses · Bruto x líquido
        </p>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-300 mt-1">
        <div className="flex items-center gap-1">
          <span className="w-3 h-1.5 rounded-full bg-emerald-400" />
          Bruto
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-1.5 rounded-full bg-sky-400" />
          Líquido
        </div>
      </div>

      <div className="mt-1 h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={mockMonthlyProfit} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid
              stroke="#1f2937"
              strokeDasharray="3 3"
              horizontal
              vertical={false}
            />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={{ stroke: "#1f2937" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#1f2937" }}
              tickLine={false}
              ticks={ticks}
              tickFormatter={(v) => formatCurrency(v).replace("R$", "")}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#020617",
                border: "1px solid #1f2937",
                borderRadius: 12,
                fontSize: 11,
                padding: 10,
              }}
              formatter={(value: any, name) => {
                const label = name === "gross" ? "Bruto" : "Líquido";
                return [formatCurrency(value as number), label];
              }}
              labelStyle={{ color: "#e5e7eb", fontWeight: 500 }}
            />
            <Line
              type="monotone"
              dataKey="gross"
              stroke="#22c55e"
              strokeWidth={2.4}
              dot={{ r: 3.8, fill: "#22c55e", strokeWidth: 0 }}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
            <Line
              type="monotone"
              dataKey="net"
              stroke="#38bdf8"
              strokeWidth={2.4}
              dot={{ r: 3.8, fill: "#38bdf8", strokeWidth: 0 }}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const WeeklyProfitChart: React.FC = () => {
  const maxValue = useMemo(
    () =>
      mockWeeklyProfit.reduce(
        (max, item) => Math.max(max, item.gross, item.net),
        0
      ),
    []
  );

  const step = Math.ceil(maxValue / 5 / 200) * 200 || 500;
  const ticks = Array.from({ length: 6 }, (_, i) => i * step);

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl px-5 py-4 flex flex-col gap-2 shadow-lg shadow-black/30">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-100">Lucro semanal</p>
        <p className="text-[11px] text-slate-400">
          Últimas 4 semanas · Bruto x líquido
        </p>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-300 mt-1">
        <div className="flex items-center gap-1">
          <span className="w-3 h-1.5 rounded-full bg-emerald-400" />
          Bruto
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-1.5 rounded-full bg-sky-400" />
          Líquido
        </div>
      </div>

      <div className="mt-1 h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={mockWeeklyProfit} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid
              stroke="#1f2937"
              strokeDasharray="3 3"
              horizontal
              vertical={false}
            />
            <XAxis
              dataKey="weekLabel"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={{ stroke: "#1f2937" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#1f2937" }}
              tickLine={false}
              ticks={ticks}
              tickFormatter={(v) => formatCurrency(v).replace("R$", "")}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#020617",
                border: "1px solid #1f2937",
                borderRadius: 12,
                fontSize: 11,
                padding: 10,
              }}
              formatter={(value: any, name) => {
                const label = name === "gross" ? "Bruto" : "Líquido";
                return [formatCurrency(value as number), label];
              }}
              labelStyle={{ color: "#e5e7eb", fontWeight: 500 }}
            />
            <Bar
              dataKey="gross"
              stackId="a"
              radius={[8, 8, 0, 0]}
              fill="#22c55e"
            />
            <Bar
              dataKey="net"
              stackId="b"
              radius={[8, 8, 0, 0]}
              fill="#38bdf8"
            />
            <Legend
              wrapperStyle={{
                fontSize: 11,
                color: "#e5e7eb",
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const OrdersTable: React.FC<{ orders: Order[] }> = ({ orders }) => {
  const last10 = orders.slice(0, 10);

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl px-5 py-4 shadow-lg shadow-black/30">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-100">Pedidos</p>
          <p className="text-[11px] text-slate-400">
            Listando os últimos 10 pedidos
          </p>
        </div>
        <button className="px-3 py-1.5 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/60 hover:bg-emerald-500/20 transition-colors">
          Ver mais
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs text-left">
          <thead>
            <tr className="border-b border-slate-800/80 text-[11px] text-slate-400 uppercase">
              <th className="py-2 pr-3">Data</th>
              <th className="py-2 pr-3">Pedido</th>
              <th className="py-2 pr-3">Produto</th>
              <th className="py-2 pr-3">Variação</th>
              <th className="py-2 pr-3 text-right">Valor</th>
              <th className="py-2 pr-3 text-right">Taxas Shopee</th>
              <th className="py-2 pr-3 text-right">Valor liberado</th>
              <th className="py-2 pr-3 text-right">Custos</th>
              <th className="py-2 pr-3 text-right">Lucro</th>
              <th className="py-2 pr-3 text-right">Margem</th>
            </tr>
          </thead>
          <tbody>
            {last10.map((order) => {
              const net = order.releasedValue - order.cost;
              const margin = calcMargin(net, order.releasedValue);

              return (
                <tr
                  key={order.id}
                  className="border-b border-slate-800/70 last:border-0 hover:bg-slate-900/80 transition-colors"
                >
                  <td className="py-2 pr-3 text-slate-300">
                    {new Date(order.date).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="py-2 pr-3 text-slate-200 font-medium">
                    {order.orderNumber}
                  </td>
                  <td className="py-2 pr-3 text-slate-300">{order.product}</td>
                  <td className="py-2 pr-3 text-slate-300">
                    {order.variation}
                  </td>
                  <td className="py-2 pr-3 text-right text-slate-200">
                    {formatCurrency(order.value)}
                  </td>
                  <td className="py-2 pr-3 text-right text-rose-400">
                    {formatCurrency(order.shopeeFees)}
                  </td>
                  <td className="py-2 pr-3 text-right text-emerald-400">
                    {formatCurrency(order.releasedValue)}
                  </td>
                  <td className="py-2 pr-3 text-right text-slate-300">
                    {formatCurrency(order.cost)}
                  </td>
                  <td className="py-2 pr-3 text-right text-emerald-400">
                    {formatCurrency(net)}
                  </td>
                  <td className="py-2 pr-3 text-right text-slate-300">
                    {margin.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ----------------- App principal -----------------

const App: React.FC = () => {
  const [period, setPeriod] = useState<PeriodKey>("today");
  const [customRange, setCustomRange] = useState<DateRange | null>(null);

  // por enquanto filtramos os pedidos só por período básico,
  // depois, com a API, isso fica 100% real oficial
  const filteredOrders = useMemo(() => {
    if (period === "today") {
      const today = new Date().toISOString().slice(0, 10);
      return mockOrders.filter((o) => o.date === today);
    }

    if (period === "7d" || period === "30d") {
      const days = period === "7d" ? 7 : 30;
      const limit = new Date();
      limit.setDate(limit.getDate() - days);
      return mockOrders.filter((o) => new Date(o.date) >= limit);
    }

    if (period === "month") {
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      return mockOrders.filter((o) => {
        const d = new Date(o.date);
        return d.getMonth() === month && d.getFullYear() === year;
      });
    }

    if (period === "custom" && customRange?.start && customRange.end) {
      const start = new Date(customRange.start);
      const end = new Date(customRange.end);
      return mockOrders.filter((o) => {
        const d = new Date(o.date);
        return d >= start && d <= end;
      });
    }

    return mockOrders;
  }, [period, customRange]);

  const totalGross = filteredOrders.reduce((sum, o) => sum + o.releasedValue, 0);
  const totalCost = filteredOrders.reduce((sum, o) => sum + o.cost, 0);
  const totalNet = totalGross - totalCost;

  // Pedidos para enviar hoje (5 dias após venda)
  const today = new Date();
  const toShipToday = mockOrders.filter((order) => {
    const d = new Date(order.date);
    d.setDate(d.getDate() + 5);
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <div className="max-w-6xl mx-auto px-4 pb-10 pt-6">
        {/* Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-5">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-slate-50 tracking-tight">
              Luartes Shopee Dashboard
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Visão geral de saldos, faturamento, custos e lucro da Shopee.
            </p>
          </div>

          <div className="flex flex-col items-start sm:items-end gap-2">
            <PeriodSelector
              period={period}
              onChange={setPeriod}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
            />
          </div>
        </header>

        {/* Cards principais */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <StatCard
            title="Saldo disponível para saque"
            main={formatCurrency(mockSaldoDisponivel)}
            helper="↑ 18% em relação ao período anterior"
            helperColor="green"
          />

          <StatCard
            title="Pedidos no período"
            main={filteredOrders.length.toString()}
            helper={`Baseado no filtro selecionado`}
            helperColor="muted"
          />

          <StatCard
            title="Faturamento do período (Bruto)"
            main={formatCurrency(totalGross)}
            helper={`Lucro líquido: ${formatCurrency(totalNet)}`}
            helperColor={totalNet >= 0 ? "green" : "red"}
          />

          <StatCard
            title="Pedidos para enviar hoje"
            main={toShipToday.length.toString()}
            helper="Calculado com base em 5 dias de produção"
            helperColor="muted"
          />
        </section>

        {/* Seção SALDOS */}
        <section className="mb-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Saldos
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Saldo disponível"
              main={formatCurrency(mockSaldoDisponivel)}
              helper="Disponível para saque imediato"
              helperColor="green"
            />
            <StatCard
              title="Saldo a liberar"
              main={formatCurrency(mockSaldoALiberar)}
              helper="Pedidos ainda em prazo de liberação"
            />
            <StatCard
              title="Previsão próximo saque"
              main={formatCurrency(mockPrevisaoProximoSaque)}
              helper="Estimado considerando ticket médio atual"
            />
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl px-5 py-4 shadow-lg shadow-black/30 flex flex-col justify-between">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Últimos saques
              </p>
              <ul className="mt-2 space-y-1 text-[11px] text-slate-300">
                {mockSaques.map((s) => (
                  <li key={s.date} className="flex justify-between">
                    <span>{s.date}</span>
                    <span className="text-slate-100">
                      {formatCurrency(s.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Gráficos */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <MonthlyProfitChart />
          </div>
          <div className="lg:col-span-1">
            <WeeklyProfitChart />
          </div>
        </section>

        {/* Tabela de pedidos */}
        <OrdersTable orders={mockOrders} />
      </div>
    </div>
  );
};

export default App;
